import React from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  FiX,
  FiMinus,
  FiPlus,
  FiTrash2,
  FiShoppingBag,
  FiArrowLeft,
  FiArrowRight,
} from "react-icons/fi";

import {
  removeFromOrder,
  updateOrderItem,
} from "../../redux/actions/order";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  media,
} from "../../server";


function OrderDrawer({
  open,
  onClose,
}) {
  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const { t } =
    useTranslation();

  const {
    currentDir,
  } = useStateContext();

  const items = useSelector(
    (state) =>
      state.Order?.items || []
  );

  const DirectionArrow =
    currentDir === "rtl"
      ? FiArrowLeft
      : FiArrowRight;


  const total = items.reduce(
    (sum, item) =>
      sum +
      Number(item.price || 0) *
        Number(
          item.quantity || 1
        ),
    0
  );


  const updateQuantity = (
    item,
    quantity
  ) => {
    if (quantity < 1) return;

    dispatch(
      updateOrderItem(
        item.lineId,
        {
          quantity,
        }
      )
    );
  };


  const updateNotes = (
    item,
    notes
  ) => {
    dispatch(
      updateOrderItem(
        item.lineId,
        {
          notes,
        }
      )
    );
  };


  const goCheckout = () => {
    if (!items.length) return;

    onClose();

    navigate("/Checkout");

    window.scrollTo(0, 0);
  };


  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}

          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            onClick={onClose}
            className="
              fixed
              inset-0

              z-[1200]

              bg-color-2/55
              backdrop-blur-sm
            "
          />


          {/* Drawer */}

          <motion.aside
            initial={{
              x:
                currentDir === "rtl"
                  ? "105%"
                  : "-105%",
            }}
            animate={{
              x: 0,
            }}
            exit={{
              x:
                currentDir === "rtl"
                  ? "105%"
                  : "-105%",
            }}
            transition={{
              type: "spring",
              stiffness: 280,
              damping: 30,
            }}
            className={`
              fixed
              top-0
              bottom-0

              z-[1201]

              w-[92%]
              max-w-[440px]

              flex
              flex-col

              bg-back-primery
              dark:bg-back-darksecondary

              shadow-[0_0_80px_rgba(0,0,0,.30)]

              ${
                currentDir ===
                "rtl"
                  ? "right-0"
                  : "left-0"
              }
            `}
          >

            {/* Header */}

            <div
              className="
                flex
                items-center
                justify-between

                p-5

                border-b
                border-color-2/10
                dark:border-white/10
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-3
                "
              >
                <div
                  className="
                    flex
                    w-11
                    h-11

                    items-center
                    justify-center

                    rounded-full

                    bg-color-1/10
                    text-color-1
                  "
                >
                  <FiShoppingBag />
                </div>

                <div>
                  <h2
                    className="
                      font-kufi
                      font-bold

                      text-xl

                      text-tcolor-4
                      dark:text-white
                    "
                  >
                    {t("your order")}
                  </h2>

                  <span
                    className="
                      font-kufi
                      text-xs

                      text-n-5
                    "
                  >
                    {items.length}{" "}
                    {t("items")}
                  </span>
                </div>
              </div>


              <button
                type="button"
                onClick={onClose}
                className="
                  flex
                  w-10
                  h-10

                  items-center
                  justify-center

                  rounded-full

                  bg-color-2/5
                  dark:bg-white/5

                  text-tcolor-4
                  dark:text-white
                "
              >
                <FiX />
              </button>
            </div>


            {/* Items */}

            <div
              className="
                flex-1
                overflow-y-auto

                p-4

                space-y-4
              "
            >
              {items.length === 0 ? (

                <div
                  className="
                    h-full

                    flex
                    flex-col

                    items-center
                    justify-center

                    text-center

                    px-5
                  "
                >
                  <div
                    className="
                      flex
                      w-20
                      h-20

                      items-center
                      justify-center

                      rounded-full

                      bg-color-1/10

                      text-color-1
                      text-3xl

                      mb-5
                    "
                  >
                    <FiShoppingBag />
                  </div>

                  <h3
                    className="
                      font-kufi
                      font-semibold

                      text-lg

                      text-tcolor-4
                      dark:text-white

                      mb-2
                    "
                  >
                    {t(
                      "your order is empty"
                    )}
                  </h3>

                  <p
                    className="
                      font-kufi
                      text-sm

                      text-n-5
                    "
                  >
                    {t(
                      "add meals to start your order"
                    )}
                  </p>
                </div>

              ) : (

                items.map(
                  (item) => {
                    const title =
                      currentDir ===
                      "ltr"
                        ? item.titleEn
                        : item.titleAr;

                    return (
                      <div
                        key={
                          item.lineId
                        }
                        className="
                          rounded-[1.5rem]

                          border
                          border-color-2/10
                          dark:border-white/10

                          bg-back-secondary
                          dark:bg-back-darkprimery

                          p-3
                        "
                      >

                        <div
                          className="
                            flex
                            gap-3
                          "
                        >

                          {/* Image */}

                          <img
                            src={`${media}${item.Image}`}
                            alt={title}
                            className="
                              w-20
                              h-20

                              shrink-0

                              rounded-[1rem]

                              object-cover
                            "
                          />


                          <div
                            className="
                              flex-1
                              min-w-0
                            "
                          >

                            <div
                              className="
                                flex
                                items-start
                                justify-between

                                gap-2
                              "
                            >
                              <h3
                                className="
                                  font-kufi
                                  font-semibold

                                  text-sm

                                  text-tcolor-4
                                  dark:text-white

                                  line-clamp-2
                                "
                              >
                                {title}
                              </h3>


                              <button
                                type="button"
                                onClick={() =>
                                  dispatch(
                                    removeFromOrder(
                                      item.lineId
                                    )
                                  )
                                }
                                className="
                                  shrink-0

                                  text-red-500

                                  p-1
                                "
                              >
                                <FiTrash2 />
                              </button>
                            </div>


                            <div
                              className="
                                flex
                                items-end
                                gap-1

                                mt-2
                              "
                            >
                              <span
                                className="
                                  font-bold
                                  text-color-1
                                "
                              >
                                {item.price *
                                  item.quantity}
                              </span>

                              <span
                                className="
                                  font-kufi
                                  text-[10px]
                                  text-color-1
                                "
                              >
                                {t("currency")}
                              </span>
                            </div>

                          </div>

                        </div>


                        {/* Quantity */}

                        <div
                          className="
                            flex
                            items-center
                            justify-between

                            mt-4
                          "
                        >
                          <span
                            className="
                              font-kufi
                              text-xs

                              text-n-5
                            "
                          >
                            {t("quantity")}
                          </span>


                          <div
                            className="
                              flex
                              items-center

                              rounded-full

                              border
                              border-color-2/10
                              dark:border-white/10

                              p-1
                            "
                          >
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(
                                  item,
                                  item.quantity -
                                    1
                                )
                              }
                              className="
                                flex
                                w-8
                                h-8

                                items-center
                                justify-center

                                rounded-full

                                text-color-1
                              "
                            >
                              <FiMinus />
                            </button>


                            <span
                              className="
                                min-w-[35px]

                                text-center

                                font-bold

                                text-tcolor-4
                                dark:text-white
                              "
                            >
                              {
                                item.quantity
                              }
                            </span>


                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(
                                  item,
                                  item.quantity +
                                    1
                                )
                              }
                              className="
                                flex
                                w-8
                                h-8

                                items-center
                                justify-center

                                rounded-full

                                text-color-1
                              "
                            >
                              <FiPlus />
                            </button>
                          </div>
                        </div>


                        {/* Notes */}

                        <textarea
                          value={
                            item.notes || ""
                          }
                          onChange={(e) =>
                            updateNotes(
                              item,
                              e.target
                                .value
                            )
                          }
                          rows={2}
                          maxLength={300}
                          placeholder={t(
                            "order notes"
                          )}
                          className="
                            mt-3

                            w-full

                            resize-none

                            rounded-xl

                            border
                            border-color-2/10
                            dark:border-white/10

                            bg-back-primery
                            dark:bg-back-darksecondary

                            p-3

                            font-kufi
                            text-xs

                            text-tcolor-4
                            dark:text-white

                            outline-none

                            focus:border-color-1
                          "
                        />

                      </div>
                    );
                  }
                )
              )}
            </div>


            {/* Footer */}

            {items.length > 0 && (
              <div
                className="
                  border-t
                  border-color-2/10
                  dark:border-white/10

                  p-5
                "
              >
                <div
                  className="
                    flex
                    items-end
                    justify-between

                    mb-5
                  "
                >
                  <span
                    className="
                      font-kufi

                      text-n-6
                      dark:text-tcolor-2
                    "
                  >
                    {t("order total")}
                  </span>

                  <div
                    className="
                      flex
                      items-end
                      gap-1
                    "
                  >
                    <span
                      className="
                        text-3xl
                        font-bold

                        text-color-1
                      "
                    >
                      {total}
                    </span>

                    <span
                      className="
                        font-kufi
                        text-xs

                        text-color-1

                        mb-1
                      "
                    >
                      {t("currency")}
                    </span>
                  </div>
                </div>


                <button
                  type="button"
                  onClick={
                    goCheckout
                  }
                  className="
                    group

                    flex

                    w-full

                    items-center
                    justify-center

                    gap-3

                    rounded-full

                    bg-color-1

                    py-4

                    font-kufi
                    font-semibold

                    text-white

                    transition-colors

                    hover:bg-color-2
                  "
                >
                  {t("complete order")}

                  <DirectionArrow />
                </button>
              </div>
            )}

          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export default OrderDrawer;